import { useMemo, useState } from "react";
import { buildElevationProfile, computeSurfaceStats } from "../lib/track-utils";
import { useLang } from "../contexts/LanguageContext";

const PAVED_COLOR = "#3b82f6"; // asphalt → blue
const UNPAVED_COLOR = "#ff7a18"; // unpaved → orange

export default function ElevationProfile({ points, onHover, onHide }) {
    const { t } = useLang();
    const profile = useMemo(() => buildElevationProfile(points), [points]);
    const surface = useMemo(() => computeSurfaceStats(points), [points]);
    const [hover, setHover] = useState(null);

    const hasData = profile.some((p) => typeof p.ele === "number");
    const hasSurface = profile.some((p) => p.paved !== undefined);
    const unpavedPct =
        typeof surface.unpavedPct === "number"
            ? Math.round(surface.unpavedPct)
            : null;
    const pavedPct = unpavedPct === null ? null : 100 - unpavedPct;

    const W = 600;
    const H = 70;
    const pad = 4;

    const totalD = profile[profile.length - 1]?.d || 1;
    const withEle = profile.filter((p) => typeof p.ele === "number");
    const eles = withEle.map((p) => p.ele);
    const minE = eles.length ? Math.min(...eles) : 0;
    const maxE = eles.length ? Math.max(...eles) : 0;
    const rangeE = Math.max(1, maxE - minE);

    const samples = withEle.map((p) => ({
        x: pad + ((W - pad * 2) * p.d) / totalD,
        y: H - pad - ((H - pad * 2) * (p.ele - minE)) / rangeE,
        paved: p.paved,
        idx: p.idx,
        ele: p.ele,
        d: p.d,
    }));

    // Build consecutive same-surface runs (matches the map colouring logic).
    const colorOf = (p) => (p.paved === true ? PAVED_COLOR : UNPAVED_COLOR);
    const runs = [];
    let cur = null;
    for (let i = 1; i < samples.length; i++) {
        const color = colorOf(samples[i]);
        if (cur && cur.color === color) {
            cur.coords.push(samples[i]);
        } else {
            cur = { color, coords: [samples[i - 1], samples[i]] };
            runs.push(cur);
        }
    }
    if (runs.length === 0 && samples.length === 1) {
        runs.push({ color: colorOf(samples[0]), coords: [samples[0]] });
    }

    const baseY = H - pad;
    const toLine = (coords) =>
        coords.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");

    const handleMove = (e) => {
        if (samples.length === 0) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const vbX = pad + ratio * (W - pad * 2);
        let best = samples[0];
        let bd = Infinity;
        for (const s of samples) {
            const dd = Math.abs(s.x - vbX);
            if (dd < bd) {
                bd = dd;
                best = s;
            }
        }
        setHover(best);
        const src = points[best.idx];
        if (onHover && src) onHover({ lat: src.lat, lng: src.lng });
    };

    const handleLeave = () => {
        setHover(null);
        if (onHover) onHover(null);
    };

    if (!hasData) return null;

    return (
        <div
            className="rf-card grain relative px-3 py-2"
            data-testid="elevation-profile"
        >
            <div className="flex items-center justify-between mb-1">
                <span className="font-display text-[10px] uppercase tracking-[0.18em] text-[var(--text-faint)]">
                    {t("elevationProfile")}
                </span>
                <div className="flex items-center gap-3">
                    {hasSurface && (
                        <span
                            className="flex items-center gap-3 text-[10px] font-mono-stat"
                            data-testid="elevation-legend"
                        >
                            <span
                                className="flex items-center gap-1"
                                style={{ color: PAVED_COLOR }}
                                data-testid="legend-paved"
                            >
                                <span
                                    className="inline-block w-2.5 h-1 rounded-full"
                                    style={{ background: PAVED_COLOR }}
                                ></span>
                                {t("legendPaved")} {pavedPct !== null ? `${pavedPct}%` : ""}
                            </span>
                            <span
                                className="flex items-center gap-1"
                                style={{ color: UNPAVED_COLOR }}
                                data-testid="legend-unpaved"
                            >
                                <span
                                    className="inline-block w-2.5 h-1 rounded-full"
                                    style={{ background: UNPAVED_COLOR }}
                                ></span>
                                {t("legendUnpaved")} {unpavedPct !== null ? `${unpavedPct}%` : ""}
                            </span>
                        </span>
                    )}
                    <span className="font-mono-stat text-[10px] text-[var(--text-faint)]">
                        {Math.round(minE)}m – {Math.round(maxE)}m
                    </span>
                    {onHide && (
                        <button
                            onClick={onHide}
                            className="text-[var(--text-faint)] hover:text-[var(--accent)] transition-colors"
                            title={t("hideElevation")}
                            data-testid="hide-elevation-btn"
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    )}
                </div>
            </div>

            <div className="relative" style={{ height: H }}>
                <svg
                    viewBox={`0 0 ${W} ${H}`}
                    preserveAspectRatio="none"
                    className="w-full"
                    style={{ height: H, cursor: "crosshair" }}
                    onMouseMove={handleMove}
                    onMouseLeave={handleLeave}
                    data-testid="elevation-svg"
                >
                    <defs>
                        <linearGradient id="pavedGrad" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor={PAVED_COLOR} stopOpacity="0.5" />
                            <stop offset="100%" stopColor={PAVED_COLOR} stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id="unpavedGrad" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor={UNPAVED_COLOR} stopOpacity="0.5" />
                            <stop offset="100%" stopColor={UNPAVED_COLOR} stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    {runs.map((run, i) => {
                        if (run.coords.length < 2) return null;
                        const first = run.coords[0];
                        const last = run.coords[run.coords.length - 1];
                        const areaPath = `${toLine(run.coords)} L${last.x},${baseY} L${first.x},${baseY} Z`;
                        const isPaved = run.color === PAVED_COLOR;
                        return (
                            <g key={i}>
                                <path
                                    d={areaPath}
                                    fill={`url(#${isPaved ? "pavedGrad" : "unpavedGrad"})`}
                                />
                                <path
                                    d={toLine(run.coords)}
                                    fill="none"
                                    stroke={run.color}
                                    strokeWidth="1.6"
                                    strokeLinejoin="round"
                                    strokeLinecap="round"
                                />
                            </g>
                        );
                    })}
                    {hover && (
                        <line
                            x1={hover.x}
                            y1={pad}
                            x2={hover.x}
                            y2={baseY}
                            stroke="rgba(255,255,255,0.6)"
                            strokeWidth="1"
                            strokeDasharray="3 2"
                            vectorEffect="non-scaling-stroke"
                        />
                    )}
                </svg>

                {hover && (
                    <>
                        <div
                            className="absolute pointer-events-none"
                            data-testid="elevation-hover-dot"
                            style={{
                                left: `${(hover.x / W) * 100}%`,
                                top: hover.y,
                                width: 9,
                                height: 9,
                                marginLeft: -4.5,
                                marginTop: -4.5,
                                borderRadius: "999px",
                                background: "#fff",
                                border: `2px solid ${hover.paved === true ? PAVED_COLOR : UNPAVED_COLOR}`,
                                boxShadow: "0 0 0 1px rgba(0,0,0,.5)",
                            }}
                        ></div>
                        <div
                            className="absolute pointer-events-none font-mono-stat text-[10px] px-1.5 py-0.5 rounded"
                            data-testid="elevation-hover-label"
                            style={{
                                left: `${(hover.x / W) * 100}%`,
                                top: -2,
                                transform:
                                    hover.x > W * 0.7
                                        ? "translateX(-105%)"
                                        : "translateX(5%)",
                                background: "var(--bg-card-solid)",
                                border: "1px solid var(--border-strong)",
                                color: "var(--text-primary)",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {Math.round(hover.ele)} m ·{" "}
                            {hover.d < 1000
                                ? `${Math.round(hover.d)} m`
                                : `${(hover.d / 1000).toFixed(2)} km`}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
